<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import type { IResponseData } from '~/types'
import { heroBanner } from '~/data/pages/cards/mocks'
import type { ICardTypeData } from '~/components/widget/cards'
import { WidgetCardsListMeiliSearch, WidgetCardsRatingCta } from '~/components/widget/cards'
import { WidgetSelectCardType } from '~/components/widget/select-card-type'
import { WidgetSeoContent, WidgetSeoHeroBanner } from '~/components/widget/seo'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { WidgetFaqList } from '~/components/widget/faq-list'
import { WidgetDiscussionsAskSpecialist } from '~/components/widget/discussions'
import { WidgetSelectCardSystem } from '~/components/widget/select-card-system'
import type { ICardsQueryParams } from '~/components/entity/card/item'

definePageMeta({
  path: '/cards',
  alias: ['/cards/:type(.*)*', '/cards/system/:system(.*)*']
})

const { t } = useI18n()
const route = useRoute()
const { pathWithoutLocale } = useLocalizedPath()

const pageInfo = reactive({
  title: t('common.bank_cards')
})

const cardsListQueryParams = reactive<ICardsQueryParams>({
  per_page: '20'
})

const typeParam = route.params.system ? 'system' : route.params.type ? 'type' : null

const paramValue = typeParam ? route.params[typeParam] : null

if (paramValue && typeParam) {
  const url = typeParam === 'system' ? `/card_systems/${paramValue}` : `/card_types/${paramValue}`

  const key = `cards-${typeParam}-${paramValue}`

  const { data } = await useApiWith404<IResponseData<ICardTypeData>>(url, {
    key
  })

  if (data.value) {
    pageInfo.title = data.value.data.title
  }
}

const breadcrumbs = computed(() => {
  const links = []

  links.push({ title: t('common.bank_cards'), url: '/cards' })
  if (paramValue && pageInfo.title) {
    links.push({ title: pageInfo.title, url: route.path })
  }

  return links
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetSelectCardType
              :query-params="{
                'filter[position]': 'sidebar'
              }"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetSelectCardSystem
              :query-params="{
                'filter[position]': 'sidebar'
              }"
            />
            <WidgetCardsRatingCta />
            <WidgetDiscussionsAskSpecialist />
          </aside>
        </template>
        <div class="space-y-5" role="main">
          <WidgetSeoContent v-if="typeParam" position="upper" :title="pageInfo.title" />
          <WidgetSeoHeroBanner
            v-if="!typeParam"
            :heading="$t(heroBanner.heading)"
            :text="$t(heroBanner.text)"
            :image="heroBanner.image"
            illustration="cards"
            backdrop="shapes"
          />
          <WidgetReviveAd zoneid="39" class="overflow-hidden rounded-lg" />

          <WidgetCardsListMeiliSearch :query-params="cardsListQueryParams" />

          <WidgetSeoContent position="bottom" />

          <WidgetFaqList
            :query-params="{
              'filter[faqable_url]': pathWithoutLocale
            }"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
